import type {
  FileFolderItem,
  FxBreaklineType,
  FxReferenceCategory,
  FxWorkspace,
  MissionItem,
  PointCloudItem,
  TrajectoryItem,
} from '@mobile-mapping/providers';
import { MapWorkspace } from '@trimble/geospatial';
import { t } from '@trimble/i18n';
import { TreeNodeDefinition } from '@trimble/ui-components/solidjs';
import {
  adaptMapWorkspaceNodesForFxReferences,
  adaptProjectDataNodesForFxReferences,
  FX_MAP_WORKSPACES_REFERENCE_CATEGORY,
} from './adapt-fx-reference-nodes';
import type {
  FxWorkspaceTreeItem,
  FxWorkspaceTreeItemKind,
} from './fx-workspace-tree.types';
import {
  buildProjectDataCategoryNodes,
  getProjectDataCategoryFolderProps,
  PROJECT_DATA_TREE_CATEGORIES,
} from './project-data-tree-nodes';
import { buildWorkspaceNodes } from './workspace-tree-nodes';

export type { FxWorkspaceTreeItem, FxWorkspaceTreeItemKind };

export interface FxWorkspaceProjectData {
  readonly missions: readonly MissionItem[];
  readonly trajectories: readonly TrajectoryItem[];
  readonly models: readonly FileFolderItem[];
  readonly pointClouds: readonly PointCloudItem[];
  readonly mapWorkspaces?: readonly MapWorkspace[];
  readonly mapLayers?: ReadonlyArray<{ id: string; name: string }>;
}

export function extractedFeaturesFolderId(workspaceId: string): string {
  return `${workspaceId}::extractedFeatures`;
}

export function breaklinesFolderId(workspaceId: string): string {
  return `${workspaceId}::extractedFeatures::breaklines`;
}

export function pciFolderId(workspaceId: string): string {
  return `${workspaceId}::extractedFeatures::pci`;
}

export function breaklineTypeNodeId(
  workspaceId: string,
  type: FxBreaklineType
): string {
  return `${workspaceId}::extractedFeatures::breaklines::${type}`;
}

export function draftLayerNodeId(workspaceId: string, layerId: string): string {
  return `${workspaceId}::extractedFeatures::draftLayers::${layerId}`;
}

export function jobItemId(workspaceId: string, jobId: string): string {
  return `${workspaceId}::extractedFeatures::${jobId}`;
}

export function isExtractedFeatureTreeNodeKind(
  kind: FxWorkspaceTreeItemKind | undefined
): kind is
  | 'extractedFeatures'
  | 'pci'
  | 'breaklines'
  | 'breaklineType'
  | 'job' {
  return (
    kind === 'extractedFeatures' ||
    kind === 'pci' ||
    kind === 'breaklines' ||
    kind === 'breaklineType' ||
    kind === 'job'
  );
}

function referenceCategoryId(
  workspaceId: string,
  category: FxReferenceCategory
): string {
  return `${workspaceId}::references::${category}`;
}

function buildMapWorkspaceReferenceNodes(
  workspaceId: string,
  projectData: FxWorkspaceProjectData
): TreeNodeDefinition<FxWorkspaceTreeItem>[] {
  const categoryNodes = adaptMapWorkspaceNodesForFxReferences(
    workspaceId,
    buildWorkspaceNodes(
      projectData.mapWorkspaces ?? [],
      projectData.mapLayers ?? [],
      { includeRootWhenEmpty: true }
    )
  );
  const categoryId = referenceCategoryId(
    workspaceId,
    FX_MAP_WORKSPACES_REFERENCE_CATEGORY
  );

  return [
    {
      id: categoryId,
      label: t('mobileMapping.workspace.title'),
      labelIcon: 'tc-icon-map',
      parentId: workspaceId,
      isLeaf: categoryNodes.length === 0,
      data: {
        kind: 'referenceCategory',
        workspaceId,
        category: FX_MAP_WORKSPACES_REFERENCE_CATEGORY,
      },
      showVisibilityToggle: false,
    },
    ...categoryNodes,
  ];
}

function buildReferenceNodes(
  workspaceId: string,
  projectData: FxWorkspaceProjectData
): TreeNodeDefinition<FxWorkspaceTreeItem>[] {
  const nodes: TreeNodeDefinition<FxWorkspaceTreeItem>[] = [
    ...buildMapWorkspaceReferenceNodes(workspaceId, projectData),
  ];

  for (const category of PROJECT_DATA_TREE_CATEGORIES) {
    const projectDataNodes = buildProjectDataCategoryNodes(
      category,
      projectData
    );
    const categoryNodes = adaptProjectDataNodesForFxReferences(
      workspaceId,
      category,
      projectDataNodes
    );
    const categoryId = referenceCategoryId(workspaceId, category);

    nodes.push({
      id: categoryId,
      ...getProjectDataCategoryFolderProps(category),
      parentId: workspaceId,
      isLeaf: categoryNodes.length === 0,
      data: { kind: 'referenceCategory', workspaceId, category },
      showVisibilityToggle: false,
    });

    nodes.push(...categoryNodes);
  }

  return nodes;
}

export const buildFxWorkspaceNodesForActive = (
  workspace: FxWorkspace,
  projectData: FxWorkspaceProjectData
): TreeNodeDefinition<FxWorkspaceTreeItem>[] => {
  const workspaceId = workspace.id;

  return buildReferenceNodes(workspaceId, projectData).map((node) =>
    node.parentId === workspaceId ? { ...node, parentId: undefined } : node
  );
};
